import type { Optional } from '@festina-life/core'
import {
  type ConstructorCall,
  type FPlusLiteralType,
  type FunctionMetadata,
  inferLiteralType,
  parseConstructorCall,
  parseConstructorCallArray,
  parseTypedCollection,
} from '@festina-life/fplus-monaco'

import {
  extractCollectionType,
  typeOptions,
  wrapperTypeToCollectionType,
} from '../../context/typeOptions'
import {
  type Cardinality,
  isModelMetaType,
  type MetaModel,
  type PropertyDescriptor,
} from '../../types/metaModel'
import {
  type ConfigurationSimpleValueField,
  type ConfigurationTemplateFunctionValue,
  isDeclaredConfigurationTemplateFunctionValue,
  isSimpleValueSetupField,
  type SimpleValueSetupField,
  type TemplateField,
} from '../../types/templateModel'
import type { EnumDefinition } from '../../uiModels/enumLiterals/types'
import { extractFunctionWithCache } from '../templateFieldsTable/functionHelpers'
import type {
  CollectionType,
  ConstructorTypeInformation,
  ExpressionTypeInformation,
  LiteralTypeInformation,
} from './types'

export const UN_TEMPLATED_SUFFIX = 'UnTemplated'
const PropertyDescriptorToLiteralTypeMapper: Record<string, FPlusLiteralType> =
  {
    Boolean: 'boolean',
    Fraction: 'fraction',
    Double: 'fraction',
    Integer: 'integer',
    String: 'string',
  }

export const sortEnumOptionsWithOptionalDotPrefix = ({
  options,
  prefixTypeName,
}: {
  options: string[]
  prefixTypeName?: Optional<string>
}) => {
  const allOptions = [...options]
  allOptions.sort((optionA, optionB) =>
    optionA.localeCompare(optionB, undefined, { sensitivity: 'base' }),
  )

  return allOptions.map((option) => `${prefixTypeName ?? ''}.${option}`)
}

const defaultValueSource = (
  field: SimpleValueSetupField | ConfigurationSimpleValueField,
) =>
  isSimpleValueSetupField(field)
    ? field.value?.defaultValue?.expression?.fplusSource
    : field.value?.expression?.fplusSource

export const getSimpleFieldTypeInformation = ({
  field,
  configuredEnums,
  metaModel,
}: {
  field: SimpleValueSetupField | ConfigurationSimpleValueField
  configuredEnums: Map<string, EnumDefinition>
  metaModel: MetaModel
}): Optional<ExpressionTypeInformation> => {
  const { type, descriptor } = extractTypeFromTemplateField(field)

  return (
    parseContructorTypeInformation({
      source: defaultValueSource(field),
      typeName: type?.typeName ?? '',
      metaModel,
      collectionType: descriptor?.cardinality === 'many' ? 'list' : undefined,
      functionMetadata: undefined,
      parent: undefined,
    }) ??
    parseSimpleFieldLiteralTypeInformation({
      field,
      configuredEnums,
      metaModel,
    })
  )
}

export const getParameterTypeInformation = ({
  parameter,
  metaModel,
  configuredEnums,
  parent,
}: {
  parameter: PropertyDescriptor & { value: Optional<string> }
  metaModel: MetaModel
  configuredEnums: Map<string, EnumDefinition>
  parent: ConstructorTypeInformation
}): Optional<ExpressionTypeInformation> =>
  parseContructorTypeInformation({
    source: parameter.value,
    typeName: parameter.typeName,
    metaModel,
    collectionType: parameter?.cardinality === 'many' ? 'list' : undefined,
    functionMetadata: undefined,
    parent,
  }) ??
  parseLiteralTypeInformation({
    cardinality: parameter?.cardinality,
    typeName: parameter.typeName,
    source: parameter.value,
    configuredEnums,
    metaModel,
    shouldPrefixEnumTypeName: true,
    parent,
  })

const parseContructorTypeInformation = ({
  source,
  typeName,
  metaModel,
  collectionType,
  functionMetadata,
  parent,
}: {
  source: Optional<string>
  typeName: Optional<string>
  metaModel: MetaModel
  collectionType: Optional<CollectionType>
  functionMetadata: Optional<FunctionMetadata>
  parent: Optional<ConstructorTypeInformation>
}): Optional<ConstructorTypeInformation> => {
  if (typeName) {
    const metaModelType =
      metaModel.types.get(typeName) ??
      metaModel.types.get(typeName.replace(UN_TEMPLATED_SUFFIX, '')) // Templated types with a type name ending in UnTemplated will appear in the metaModel without the UnTemplated suffix

    /**
     * Constructor editing is not possible for templated types.
     */
    const isTemplated =
      isModelMetaType(metaModelType) &&
      metaModelType?.stereoTypes?.includes('InternalTemplateType') &&
      !typeName.endsWith(UN_TEMPLATED_SUFFIX) &&
      !parent?.isUnTemplated

    if (metaModelType?._type === 'ModelMetaType' && !isTemplated) {
      // We assume editing isn't possible by initialising the value to undefined.
      // If parsing the source yields an empty string or a valid constructor call we will initialise the value to an array with the constructor call.
      let value: Optional<ConstructorCall[]> = undefined

      if (collectionType) {
        value = source ? parseConstructorCallArray(source) : []
      } else {
        const parsedSingleValue = source
          ? parseConstructorCall(source)
          : undefined

        if (parsedSingleValue) {
          value = [parsedSingleValue]
        } else if (!source) {
          // If no source exists we allow constructor editing by initialising an empty array.
          value = []
        }
      }

      return {
        type: 'constructor',
        name: typeName,
        parameters: metaModelType.properties,
        value,
        collectionType,
        functionMetadata,
        parent,
        isUnTemplated:
          !!parent?.isUnTemplated || typeName.endsWith(UN_TEMPLATED_SUFFIX),
      }
    }
  }

  return undefined
}

const extractTypeFromTemplateField = (field: TemplateField) => {
  const descriptor =
    field.modelPropertyDescriptor ?? field.templatePropertyDescriptor
  const type = descriptor?.onlyTemplatedType ?? descriptor?.type

  return { type, descriptor }
}

export const createLiteralTypeInformation = (
  props: ExpressionMatchingProps,
): Optional<LiteralTypeInformation> => {
  const { source, ...rest } = props
  return {
    type: 'literal',
    value: source ? matchExpressionAgainstLiteralType(props) : [],
    ...rest,
  }
}

const parseSimpleFieldLiteralTypeInformation = ({
  field,
  configuredEnums,
  metaModel,
}: {
  field: SimpleValueSetupField | ConfigurationSimpleValueField
  configuredEnums: Map<string, EnumDefinition>
  metaModel: MetaModel
}): Optional<LiteralTypeInformation> => {
  const { type, descriptor } = extractTypeFromTemplateField(field)
  const source = defaultValueSource(field)

  return parseLiteralTypeInformation({
    metaModel,
    typeName: type?.typeName ?? '',
    configuredEnums,
    cardinality: descriptor?.cardinality,
    source,
    shouldPrefixEnumTypeName: false,
    parent: undefined,
  })
}

const parseLiteralTypeInformation = ({
  metaModel,
  typeName,
  configuredEnums,
  cardinality,
  source,
  shouldPrefixEnumTypeName,
  parent,
}: {
  source: Optional<string>
  metaModel: MetaModel
  typeName: string
  configuredEnums: Map<string, EnumDefinition>
  cardinality: Optional<Cardinality>
  shouldPrefixEnumTypeName: boolean
  parent: Optional<ConstructorTypeInformation>
}) => {
  const type = metaModel.types.get(typeName)

  if (type?._type === 'EnumMetaType') {
    const allOptions = typeOptions({
      typeName,
      configuredEnums,
      metaModel,
    })

    return createLiteralTypeInformation({
      literalType: 'enum',
      collectionType: cardinality === 'many' ? 'list' : undefined,
      enumOptions: sortEnumOptionsWithOptionalDotPrefix({
        options: allOptions ?? [],
        prefixTypeName: shouldPrefixEnumTypeName ? typeName : undefined,
      }),
      source,
      parent,
    })
  } else if (type?._type === 'SimpleMetaType') {
    const simpleType = typeName
      ? PropertyDescriptorToLiteralTypeMapper[typeName]
      : undefined
    const collectionType = cardinality === 'many' ? 'list' : undefined

    return simpleType
      ? createLiteralTypeInformation({
          literalType: simpleType,
          collectionType,
          source,
          parent,
        })
      : undefined
  } else if (typeName) {
    const allOptions = typeOptions({ typeName, configuredEnums, metaModel })
    const matchedCollectionType = extractCollectionType(typeName)

    if (allOptions?.length) {
      const literalType = 'enum'
      const enumOptions = sortEnumOptionsWithOptionalDotPrefix({
        options: allOptions ?? [],
        prefixTypeName: shouldPrefixEnumTypeName ? typeName : undefined,
      })
      if (!matchedCollectionType) {
        return createLiteralTypeInformation({
          literalType,
          enumOptions,
          source,
          parent,
        })
      }

      const collectionType = matchedCollectionType.collectionType
      return createLiteralTypeInformation({
        collectionType,
        literalType: 'enum',
        enumOptions,
        source,
        parent,
      })
    }
  }

  return undefined
}

export const getFunctionTypeInformation = ({
  value,
  metaModel,
  configuredEnums,
}: {
  value: ConfigurationTemplateFunctionValue
  metaModel: MetaModel
  configuredEnums: Map<string, EnumDefinition>
}): Optional<ExpressionTypeInformation> => {
  const source =
    value.function?.fplusSource ??
    (isDeclaredConfigurationTemplateFunctionValue(value)
      ? value.descriptor?.signature
      : undefined)

  const functionMetadata = source ? extractFunctionWithCache(source) : undefined

  if (!functionMetadata) {
    return null
  }

  const constructorTypeInformation = parseContructorTypeInformation({
    source: functionMetadata.body,
    typeName: functionMetadata.internalReturnType,
    metaModel,
    collectionType: wrapperTypeToCollectionType(functionMetadata?.wrapperType),
    functionMetadata,
    parent: undefined,
  })

  if (constructorTypeInformation) {
    return constructorTypeInformation
  }

  const mappedLiteralType =
    PropertyDescriptorToLiteralTypeMapper[
      functionMetadata.internalReturnType
    ] ??
    (metaModel.types.get(functionMetadata.internalReturnType)?._type ===
    'EnumMetaType'
      ? 'enum'
      : undefined)

  const allOptions = typeOptions({
    typeName: functionMetadata.internalReturnType,
    configuredEnums,
    metaModel,
  })

  const enumOptions = sortEnumOptionsWithOptionalDotPrefix({
    options: allOptions ?? [],
  })

  return mappedLiteralType
    ? createLiteralTypeInformation({
        literalType: mappedLiteralType,
        collectionType: wrapperTypeToCollectionType(
          functionMetadata?.wrapperType,
        ),
        enumOptions,
        functionMetadata,
        source: value.function?.fplusSource,
      })
    : null
}

type ExpressionMatchingProps = {
  source: Optional<string>
  collectionType?: Optional<CollectionType>
  literalType: FPlusLiteralType
  enumOptions?: Optional<string[]>
  functionMetadata?: Optional<FunctionMetadata>
  parent?: Optional<ConstructorTypeInformation>
}

export const matchExpressionAgainstLiteralType = ({
  source,
  collectionType,
  literalType,
  enumOptions,
}: ExpressionMatchingProps): Optional<string[]> => {
  const functionMetadata = source ? extractFunctionWithCache(source) : undefined
  const matchableSource = functionMetadata ? functionMetadata.body : source

  if (!matchableSource?.trim()) {
    return []
  }

  if (collectionType) {
    const items = parseTypedCollection(matchableSource ?? '', literalType)

    if (!items) {
      return null
    }

    return items
  } else {
    const parsedType = inferLiteralType(matchableSource ?? '')

    if (parsedType === 'enum' && !enumOptions) {
      return null
    }

    if (parsedType && parsedType === literalType) {
      return [matchableSource ?? '']
    }
  }

  return null
}
